import { Plan, useCancelSubscription, useUpdateCard } from '@springbig/shared';
import { CreditCardDto } from '~/shared/CreditCardForm/hooks/useCreditCardForm';
import { useAppNavigate } from '~/pages';
import { useEffect, useState } from 'react';

type Errors = string | string[];

type ManageApiError = {
    data?: {
        errors?: Errors;
    };
};

type UseManageFlowProps = {
    plan: Plan;
    onSuccessfulCardUpdate: () => void;
    onSuccessfulCancellation: () => void;
};

const UseManageFlow = ({
    plan,
    onSuccessfulCardUpdate,
    onSuccessfulCancellation
}: UseManageFlowProps) => {

    const { params } = useAppNavigate();

    const [apiError, setApiError] = useState<Errors>(null);

    const {
        mutate: updateCard,
        isLoading: isLoadingUpdateCard,
        error: errorUpdateCard
    } = useUpdateCard();

    const {
        mutate: cancelSubscription,
        isLoading: isLoadingCancelSubscription,
        error: errorCancelSubscription
    } = useCancelSubscription();

    useEffect(() => {
        if (errorUpdateCard) {
            setApiError((errorUpdateCard as ManageApiError)?.data?.errors);
        }

        if (errorCancelSubscription) {
            setApiError((errorCancelSubscription as ManageApiError)?.data?.errors);
        }
    }, [errorUpdateCard, errorCancelSubscription]);

    const subscription = plan.subscription;

    const onSubmitCancelSubscription = (): void => {
        setApiError(null);

        const payload = {
            member_id: params.wallet_id,
            subscription_id: subscription.id,
        };

        cancelSubscription(payload, {
            onSuccess: () => {
                onSuccessfulCancellation();
            },
        });
    };

    const onSubmitUpdateCreditCard = (data: CreditCardDto): void => {
        setApiError(null);

        const payload = {
            member_id: params.wallet_id,
            plan_id: subscription.billing_schedule === 'monthly' ? plan.id : plan.yearly_id,
            subscription_id: subscription.id,
            cc_number: data.number,
            cc_name: data.name,
            cc_cvv: data.cvv,
            cc_expiry: data.expiry,
            cc_zip: data.zip,
        };

        updateCard(payload, {
            onSuccess: () => {
                onSuccessfulCardUpdate();
            },
        });
    };

    return {
        apiError,
        isLoadingUpdateCard,
        isLoadingCancelSubscription,
        onSubmitCancelSubscription,
        onSubmitUpdateCreditCard,
    };
};

export default UseManageFlow;
